import { getUser, login } from "../api/product-api";
import { GoogleCredentialInfo } from "./GoogleCredentialInfo";

declare const google: typeof import("google-one-tap");

const url = new URL(location.href);
const baseUrl = url.pathname.startsWith("/production-automation") ? "/production-automation/" : "/";

export class AuthenticationService {
    private _currentlyLoggedInUser: { name: string, email: string, picture: string } | null = null
    private credentials: GoogleCredentialInfo | null = null;
    private loginPromise = { promise: null as Promise<boolean> | null, resolve: null as ((v: boolean) => void) | null };

    get currentlyLoggedInUser() {
        return this._currentlyLoggedInUser;
    }

    onLogin?: () => void;
    forceUpdateApp?: () => void;

    constructor() {
        //@ts-ignore
        window.onGoogleLibraryLoad = async () => {
            google.accounts.id.initialize({
              // Ref: https://developers.google.com/identity/gsi/web/reference/js-reference#IdConfiguration
              client_id: "229513081028-lfod75elkno4bj29n59i4kpu3j1dugnc.apps.googleusercontent.com",
              callback: (r) => this.handleCredentialResponse(r),
              auto_select: true,
              cancel_on_tap_outside: false,
            });

            if (!this.currentlyLoggedInUser) {
                const logged = await this.checkIfLogged();

                if (!logged) {
                    google.accounts.id.prompt();
                }
            }
        };
    }

    async handleCredentialResponse(response: google.CredentialResponse) {
        console.log("handleCredentialResponse")
        let decodedToken: any | null = null;
        try {
          const split = response.credential.split(".")[1];
          const binary = atob(split);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < bytes.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          decodedToken = JSON.parse(new TextDecoder().decode(bytes));
          this.credentials = new GoogleCredentialInfo(decodedToken, response.credential);

          const result = await this.authenticate();
          this.loginPromise.resolve?.(result);
          this.onLogin?.();
        } catch (e) {
          console.error("Error while trying to decode token", e);
        }
      }

    login() {
        this.loginPromise.promise = new Promise((resolve) => this.loginPromise.resolve = resolve);
        google.accounts.id.prompt((p) => {
           if (p.isDismissedMoment() || p.isNotDisplayed() || p.isSkippedMoment()) {
            this.loginPromise.resolve?.(false);
           }
        });
        return this.loginPromise.promise;
    }


    async authenticate(into?: string) {
        if (!this.credentials) {
            return false;
        }

        try {

            const body = new FormData();
            body.set("token", this.credentials.getJwt());

            const response = await fetch(into ?? `${baseUrl}api/user/login/`, {
                method: "POST",
                body,
                credentials: "include"
            });

            await login(this.credentials.getJwt());

            if (!into) {
                if (!response.ok) {
                    this._currentlyLoggedInUser = null;
                }
                else {
                    this._currentlyLoggedInUser = await response.json();
                }

                if (this.forceUpdateApp) {
                    this.forceUpdateApp();
                }
            }

            return response.ok;
        }
        catch(ex) {
            if (!into) {
                this._currentlyLoggedInUser = null;
            }

            console.warn("Authentication failed:", ex);

            if (this.forceUpdateApp) {
                this.forceUpdateApp();
            }
        }

        return false;
    }

    async logout() {
        if(this.credentials) {
            google.accounts.id.revoke(this.credentials.getId(), done => {
                console.log(done.error);
            });
        }
        this.credentials = null;
        this._currentlyLoggedInUser = null;

        await fetch(`${baseUrl}api/user/logout`);

        await this.checkIfLogged();

        if (this.forceUpdateApp) {
            this.forceUpdateApp();
        }
    }

    async developmentAuthenticate() {
        try {
            const response = await fetch(`${baseUrl}api/user/login`, {
                method: "POST"
            });

            if (!response.ok) {
                this._currentlyLoggedInUser = null;
            }
            else {
                this._currentlyLoggedInUser = await response.json();
            }

            if (this.forceUpdateApp) {
                this.forceUpdateApp();
            }

            return response.ok;
        }
        catch(ex) {
            console.warn(ex);
        }

        return false;
    }

    async checkIfLogged() {
        try {
            const response = await fetch(`${baseUrl}api/user/`);
            await getUser();

            if (!response.ok) {
                this._currentlyLoggedInUser = null;
            }
            else {
                this._currentlyLoggedInUser = await response.json();
            }
        }
        catch(ex) {
            this._currentlyLoggedInUser = null;
        }

        if (this.forceUpdateApp) {
            this.forceUpdateApp();
        }

        return this._currentlyLoggedInUser !== null;
    }

    async generateJWTToken() {
        try {
            const response = await fetch(`${baseUrl}api/user/jwt/`);
            return await response.text();
        }
        catch(ex) {
            console.warn(ex);
            throw ex;
        }
    }
}
